import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Check, ArrowRight, Shield, Brain, Zap, Clock, Users, FileText, Loader2 } from 'lucide-react';
import { analytics } from '../../lib/utils/analytics';
import { showToast } from '../../lib/utils/toast';

interface PricingFeature {
  feature: string;
  icon: React.FC<{ className?: string }>;
  description: string;
}

interface PricingBenefit {
  title: string;
  description: string;
  icon: React.FC<{ className?: string }>;
}

export function PricingPage() {
  const [isRedirecting, setIsRedirecting] = useState(false);

  const handleSubscribe = async () => {
    try {
      setIsRedirecting(true);
      
      // Track conversion event
      analytics.track('start_trial_clicked', {
        plan: 'professional',
        price: 49.99,
      });

      // Redirect to Stripe payment link
      window.location.href = 'https://buy.stripe.com/5kA5mX5Pv3ov3Ti4gg';
    } catch (error) {
      console.error('Error redirecting to payment:', error);
      setIsRedirecting(false);
      showToast.error({
        title: 'Error',
        description: 'Failed to start trial. Please try again.',
      });
    }
  };

  const features: PricingFeature[] = [
    {
      feature: 'AI-Powered Financial Insights',
      icon: Brain,
      description: 'Get intelligent recommendations and predictions',
    },
    {
      feature: 'Real-time Cash Flow Monitoring',
      icon: Zap,
      description: 'Track your money movement as it happens',
    },
    {
      feature: 'Smart Invoice Management',
      icon: FileText,
      description: 'Automated invoice processing and tracking',
    },
    {
      feature: 'Unlimited Document Processing',
      icon: Clock,
      description: 'Process as many documents as you need',
    },
    {
      feature: 'Priority Customer Support',
      icon: Users,
      description: '24/7 priority access to our support team',
    },
    {
      feature: 'Bank-Grade Security',
      icon: Shield,
      description: 'Enterprise-level data protection',
    },
  ];

  const benefits: PricingBenefit[] = [
    {
      title: 'Risk-Free Trial',
      description: 'Try all features free for 7 days. Cancel anytime during trial.',
      icon: Shield,
    },
    {
      title: 'No Hidden Fees',
      description: 'Transparent pricing with no surprise charges or hidden costs.',
      icon: Check,
    },
    {
      title: 'Monthly Billing',
      description: 'Flexible monthly billing with easy cancellation.',
      icon: ArrowRight,
    },
  ];

  return (
    <section 
      id="pricing" 
      className="bg-dark-bg py-24 sm:py-32 scroll-mt-20"
      aria-label="Pricing plans"
    >
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="mx-auto max-w-4xl text-center">
          <motion.h2
            className="text-4xl font-bold tracking-tight text-dark-text sm:text-5xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Simple, Transparent Pricing
          </motion.h2>
          <motion.p
            className="mt-6 text-lg leading-8 text-dark-muted"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            Start managing your finances smarter with our AI-powered platform. Try it free for 7
            days with our risk-free trial.
          </motion.p>
        </div>

        <motion.div
          className="mx-auto mt-16 max-w-2xl rounded-3xl ring-2 ring-primary-400/10 sm:mt-20 lg:mx-0 lg:flex lg:max-w-none"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="p-8 sm:p-10 lg:flex-auto">
            <h3 className="text-2xl font-bold tracking-tight text-dark-text">Professional Plan</h3>
            <p className="mt-6 text-base leading-7 text-dark-muted">
              Get access to all our premium features and take your financial management to the next
              level with AI-powered insights.
            </p>
            <div className="mt-10 flex items-center gap-x-4">
              <h4 className="flex-none text-sm font-semibold leading-6 text-primary-400">
                What's included
              </h4>
              <div className="h-px flex-auto bg-dark-border"></div>
            </div>
            <ul
              role="list"
              className="mt-8 grid grid-cols-1 gap-4 text-sm leading-6 text-dark-muted sm:grid-cols-2 sm:gap-6"
            >
              {features.map(item => (
                <li key={item.feature} className="flex gap-x-3">
                  <item.icon 
                    className="h-6 w-5 flex-none text-primary-400" 
                    aria-hidden="true"
                  />
                  <div>
                    <strong className="font-semibold text-dark-text">{item.feature}</strong>
                    <br />
                    {item.description}
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="-mt-2 p-2 lg:mt-0 lg:w-full lg:max-w-md lg:flex-shrink-0">
            <div className="rounded-2xl bg-dark-card py-10 text-center ring-1 ring-inset ring-dark-border lg:flex lg:flex-col lg:justify-center lg:py-16">
              <div className="mx-auto max-w-xs px-8">
                <p className="text-base font-semibold text-dark-text">
                  Pay monthly, cancel anytime
                </p>
                <p className="mt-6 flex items-baseline justify-center gap-x-2">
                  <span className="text-5xl font-bold tracking-tight text-dark-text">$49.99</span>
                  <span className="text-sm font-semibold leading-6 tracking-wide text-dark-muted">
                    /month
                  </span>
                </p>
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  className="mt-10 block w-full rounded-md bg-primary-600 px-3 py-2 text-center text-sm font-semibold text-dark-text shadow-sm hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  onClick={handleSubscribe}
                  disabled={isRedirecting}
                  aria-label="Start 7-day free trial"
                >
                  {isRedirecting ? (
                    <span className="flex items-center justify-center">
                      <Loader2 className="h-4 w-4 animate-spin mr-2" />
                      Redirecting...
                    </span>
                  ) : (
                    'Start 7-Day Free Trial'
                  )}
                </motion.button>
                <p className="mt-6 text-xs leading-5 text-dark-muted">
                  Credit card required. Cancel anytime during trial.
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="mt-20"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
        >
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            {benefits.map(item => (
              <div key={item.title} className="bg-dark-card rounded-lg p-6 text-center">
                <item.icon 
                  className="h-8 w-8 mx-auto text-primary-400 mb-4" 
                  aria-hidden="true"
                />
                <h4 className="text-lg font-semibold text-dark-text mb-2">{item.title}</h4>
                <p className="text-dark-muted">{item.description}</p>
              </div>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}